"use client";

import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";
import { Lightbox } from "./Lightbox";
import { OEUVRES, type Categorie, type Oeuvre } from "@/lib/diwline-data";

const CATEGORIES: ("Toutes" | Categorie)[] = [
  "Toutes",
  "Huilerie",
  "Agriculture",
  "Produits",
  "Portraits Royaux",
  "Gastronomie",
  "Célébrations",
  "Digital & Innovation",
  "Symboles",
];

export function ToutesOeuvres() {
  const [filtre, setFiltre] = useState<(typeof CATEGORIES)[number]>("Toutes");
  const [liste, setListe] = useState<Oeuvre[] | null>(null);
  const [idx, setIdx] = useState<number | null>(null);

  const filtrées = useMemo(
    () => (filtre === "Toutes" ? OEUVRES : OEUVRES.filter(o => o.categorie === filtre)),
    [filtre]
  );

  const ouvrir = (i: number) => {
    setListe(filtrées);
    setIdx(i);
  };

  const nav = (d: number) =>
    setIdx(cur => {
      if (cur === null || !liste) return null;
      return (cur + d + liste.length) % liste.length;
    });

  return (
    <section id="oeuvres" className="relative bg-royal-brown py-24 md:py-32 bg-arabesque" aria-label="Toutes les œuvres royales">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          numero="XI"
          titre="Toutes les Œuvres"
          intro="Parcourez l'ensemble des œuvres royales par catégorie : huilerie, agriculture, produits, Sa Majesté Adjia Daba et inspirations nationales."
        />

        {/* Filtres */}
        <Reveal className="mb-10">
          <div className="flex flex-wrap justify-center gap-2" role="tablist" aria-label="Filtrer les œuvres par catégorie">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={filtre === cat}
                onClick={() => setFiltre(cat)}
                className={`rounded-full border px-4 py-2 text-[11.5px] font-semibold uppercase tracking-[0.16em] transition-all ${
                  filtre === cat
                    ? "border-gold bg-gold text-royal shadow-[0_6px_24px_rgba(212,175,55,0.3)]"
                    : "border-gold/30 text-gold-light/85 hover:border-gold/70 hover:bg-gold/10"
                }`}
              >
                {cat}
                <span className={`ml-2 text-[10px] ${filtre === cat ? "text-royal/70" : "text-cream/40"}`}>
                  {cat === "Toutes" ? OEUVRES.length : OEUVRES.filter(o => o.categorie === cat).length}
                </span>
              </button>
            ))}
          </div>
        </Reveal>

        {/* Grille animée */}
        <motion.div layout className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {filtrées.map((o, i) => (
              <motion.button
                layout
                key={o.src}
                type="button"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => ouvrir(i)}
                className="group relative overflow-hidden rounded-sm border border-gold/15 focus-visible:outline-gold"
                aria-label={`Agrandir : ${o.titre}`}
              >
                <img
                  src={o.src}
                  alt={o.titre}
                  loading="lazy"
                  className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-108"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <span className="absolute bottom-2.5 left-3 right-3 opacity-0 transition-all duration-300 group-hover:opacity-100">
                  <span className="block text-[9.5px] uppercase tracking-[0.22em] text-gold/90">
                    {o.categorie}
                  </span>
                  <span className="mt-0.5 block text-left text-[11.5px] font-medium leading-snug text-cream">
                    {o.titre}
                  </span>
                </span>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Lightbox
        oeuvre={idx !== null && liste ? liste[idx] : null}
        onClose={() => { setIdx(null); setListe(null); }}
        onPrev={() => nav(-1)}
        onNext={() => nav(1)}
      />
    </section>
  );
}
